import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, KORT_PADDING, MODAL_BREDDE, fontStr } from '@/config';
import { gameState } from '@/state/store';
import type { Tier } from '@/state/GameState';
import type { Author, AuthorStats, Course } from '@/data/types';
import { courseCatalog, STAT_LABEL } from '@/data/courseCatalog';
import { initialerAv, portrettFor } from '@/data/portretter';
import { lojalitetVisning } from '@/systems/lojalitet';
import { ForfatterKarriereModal } from './ForfatterKarriereModal';
import { animateModalGlide } from '@/ui/modalGlide';
import { lagRundetRekt } from '@/ui/RoundedRect';
import { modalFokusTrap } from '@/ui/ModalFokusTrap';

const SHEET_WIDTH = MODAL_BREDDE;
const ROW_WIDTH = MODAL_BREDDE - 60;
// Runde 45: normalisert kort-internt padding (var 24).
const ROW_PADDING = KORT_PADDING;
const ROW_GAP = 18;

const STAT_RAD_1: ReadonlyArray<keyof AuthorStats> = ['dialog', 'plot', 'karakter', 'spraak'];
const STAT_RAD_2: ReadonlyArray<keyof AuthorStats> = ['voice', 'autentisitet', 'disiplin'];

const STAT_FARGE: Record<keyof AuthorStats, number> = {
  dialog: COLORS.wine,
  plot: COLORS.wine,
  karakter: COLORS.wine,
  spraak: COLORS.wine,
  voice: COLORS.wine,
  autentisitet: COLORS.wine,
  disiplin: COLORS.wine,
};

const STAT_FARGE_STERK = 0x3d5a4d;
const STAT_FARGE_SVAK = 0xa85d65;

function fargeForVerdi(k: keyof AuthorStats, verdi: number): number {
  if (verdi >= 70) return STAT_FARGE_STERK;
  if (verdi < 40) return STAT_FARGE_SVAK;
  return STAT_FARGE[k];
}

export class ForfatterModal {
  private layer?: Phaser.GameObjects.Container;
  private unsubscribe?: () => void;
  private kursLayer?: Phaser.GameObjects.Container;
  private confirmLayer?: Phaser.GameObjects.Container;
  private scrollY = 0;
  private scrollMax = 0;
  private karriereModal?: ForfatterKarriereModal;

  constructor(private scene: Phaser.Scene) {}

  open(): void {
    if (this.layer) return;
    this.layer = this.scene.add.container(0, 0).setDepth(120);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.55)
      .setOrigin(0, 0)
      .setInteractive();
    this.layer.add(backdrop);

    this.scene.input.on('wheel', this.onWheel, this);
    this.render();
    animateModalGlide(this.scene, this.layer, { ariaTittel: 'Forfattere' });
    this.unsubscribe = gameState.subscribe(() => this.render());
  }

  close(): void {
    if (!this.layer) return;
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    this.scene.input.off('wheel', this.onWheel, this);
    this.closeKursModal();
    this.closeConfirm();
    this.closeKarriereModal();
    this.scrollY = 0;
    this.layer?.destroy(true);
    this.layer = undefined;
    modalFokusTrap.lukk();
  }

  get isOpen(): boolean {
    return !!this.layer;
  }

  private render(): void {
    if (!this.layer) return;
    while (this.layer.list.length > 1) {
      const last = this.layer.list[this.layer.list.length - 1];
      this.layer.remove(last, true);
    }

    const snap = gameState.snapshot;
    const forfattere = Object.values(snap.authors);

    const sheetX = (GAME_WIDTH - SHEET_WIDTH) / 2;
    const sheetY = 96;
    const sheetH = GAME_HEIGHT - 192;

    const sheet = lagRundetRekt(
      this.scene,
      sheetX,
      sheetY,
      SHEET_WIDTH,
      sheetH,
      COLORS.beige,
      0.98,
      14,
    ).setStrokeStyle(3, COLORS.wine, 0.6);
    this.layer.add(sheet);

    const headerY = 132;
    const header = this.scene.add
      .text(sheetX + ROW_PADDING, headerY, `Mine forfattere (${forfattere.length})`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(46),
        color: '#862736',
        wordWrap: { width: SHEET_WIDTH - ROW_PADDING * 2 - 110 },
      })
      .setOrigin(0, 0);
    this.layer.add(header);

    const subtitle = this.scene.add
      .text(
        sheetX + ROW_PADDING,
        headerY + 84,
        `Dag ${snap.dag} · ${snap.kroner.toLocaleString('nb-NO')} kr`,
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(22),
          color: '#3a2e22',
        },
      )
      .setOrigin(0, 0);
    this.layer.add(subtitle);

    const closeBtn = this.scene.add
      .text(sheetX + SHEET_WIDTH - ROW_PADDING, headerY - 4, '×', {
        fontFamily: FONTS.display,
        fontSize: fontStr(72),
        color: '#1a1a1a',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    closeBtn.on('pointerup', () => this.close());
    this.layer.add(closeBtn);

    const listX = sheetX + (SHEET_WIDTH - ROW_WIDTH) / 2;
    const listTop = headerY + 160;
    const listBunn = sheetY + sheetH - 32;
    const listHoyde = listBunn - listTop;

    if (forfattere.length === 0) {
      const tomBoks = this.scene.add
        .rectangle(listX, listTop, ROW_WIDTH, 220, COLORS.white, 0.9)
        .setOrigin(0, 0)
        .setStrokeStyle(2, COLORS.oakDark, 0.4);
      const tomTxt = this.scene.add
        .text(
          listX + ROW_WIDTH / 2,
          listTop + 110,
          'Ingen forfattere ennå.\nTilby en kontrakt fra innboksen.',
          {
            fontFamily: FONTS.body,
            fontSize: fontStr(26),
            color: '#1a1a1a',
            align: 'center',
          },
        )
        .setOrigin(0.5);
      this.layer.add([tomBoks, tomTxt]);
      return;
    }

    const listContainer = this.scene.add.container(0, 0);
    this.layer.add(listContainer);

    const maske = this.scene.make.graphics({ x: 0, y: 0 }, false);
    maske.fillStyle(0xffffff);
    maske.fillRect(listX - 4, listTop, ROW_WIDTH + 8, listHoyde);
    const maskeObj = maske.createGeometryMask();
    listContainer.setMask(maskeObj);

    let y = listTop - this.scrollY;
    let bygdHoyde = 0;

    for (const forfatter of forfattere) {
      const antallBoker = this.tellBoker(snap.iProduksjon, forfatter.id) +
        this.tellBoker(snap.utgitteBoker, forfatter.id);
      const tier = this.finnHoyesteTier(snap.iProduksjon, snap.utgitteBoker, forfatter.id);
      const rad = this.buildRad(listX, y, forfatter, antallBoker, snap.dag, tier);
      listContainer.add(rad.container);
      y += rad.height + ROW_GAP;
      bygdHoyde += rad.height + ROW_GAP;
    }

    this.scrollMax = Math.max(0, bygdHoyde - listHoyde);
    if (this.scrollY > this.scrollMax) {
      this.scrollY = this.scrollMax;
    }

    this.bindScroll(listX, listTop, listHoyde);
  }

  private bindScroll(listX: number, listTop: number, listHoyde: number): void {
    if (!this.layer) return;
    if (this.scrollMax <= 0) return;
    const scrollSone = this.scene.add
      .rectangle(listX, listTop, ROW_WIDTH, listHoyde, 0x000000, 0)
      .setOrigin(0, 0)
      .setInteractive();
    let dragStart = 0;
    let scrollStart = 0;
    scrollSone.on('pointerdown', (p: Phaser.Input.Pointer) => {
      dragStart = p.y;
      scrollStart = this.scrollY;
    });
    scrollSone.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!p.isDown) return;
      const delta = dragStart - p.y;
      this.scrollY = Math.max(0, Math.min(this.scrollMax, scrollStart + delta));
      this.render();
    });
    this.layer.add(scrollSone);
  }

  private onWheel = (
    _pointer: Phaser.Input.Pointer,
    _objs: Phaser.GameObjects.GameObject[],
    _dx: number,
    dy: number,
  ): void => {
    if (!this.layer || this.scrollMax <= 0) return;
    this.scrollY = Math.max(0, Math.min(this.scrollMax, this.scrollY + dy));
    this.render();
  };

  private buildRad(
    x: number,
    y: number,
    forfatter: Author,
    antallBoker: number,
    dag: number,
    bokTier: Tier,
  ): { container: Phaser.GameObjects.Container; height: number } {
    const container = this.scene.add.container(x, y);
    const høyde = 440;

    const bg = lagRundetRekt(
      this.scene,
      0,
      0,
      ROW_WIDTH,
      høyde,
      COLORS.white,
      1,
      8,
    ).setStrokeStyle(2, COLORS.oakDark, 0.4);
    container.add(bg);

    const portrettSize = 110;
    const portrettX = ROW_PADDING + portrettSize / 2;
    const portrettY = 24 + portrettSize / 2;
    this.byggPortrett(container, portrettX, portrettY, portrettSize / 2, forfatter, bokTier);

    const tekstX = ROW_PADDING + portrettSize + 24;

    const navnTxt = this.scene.add
      .text(tekstX, 20, forfatter.navn, {
        fontFamily: FONTS.display,
        fontSize: fontStr(28),
        color: '#1a1a1a',
        wordWrap: { width: ROW_WIDTH - tekstX - ROW_PADDING - 220 },
      })
      .setOrigin(0, 0);
    container.add(navnTxt);

    if (forfatter.pseudonym) {
      const pseudo = this.scene.add
        .text(tekstX + navnTxt.width + 10, 32, '(pseudonym)', {
          fontFamily: FONTS.body,
          fontSize: fontStr(16),
          color: '#6b6b6b',
          fontStyle: 'italic',
        })
        .setOrigin(0, 0);
      container.add(pseudo);
    }

    const sjangerBg = this.scene.add
      .rectangle(tekstX, 80, 0, 0, COLORS.oakLight, 1)
      .setOrigin(0, 0);
    const sjangerTxt = this.scene.add
      .text(tekstX + 10, 86, forfatter.primaerSjanger, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(16),
        color: '#3a2410',
        fontStyle: '600',
      })
      .setOrigin(0, 0);
    sjangerBg.setSize(sjangerTxt.width + 20, sjangerTxt.height + 12);
    container.add([sjangerBg, sjangerTxt]);

    const statusPille = this.byggStatusPille(ROW_WIDTH - ROW_PADDING, 20, forfatter, dag);
    container.add(statusPille);

    const statY = 148;
    const stats1 = this.byggStatRad(ROW_PADDING, statY, STAT_RAD_1, forfatter.stats);
    container.add(stats1);
    const stats2 = this.byggStatRad(ROW_PADDING, statY + 60, STAT_RAD_2, forfatter.stats);
    container.add(stats2);

    const bokerTxt = this.scene.add
      .text(ROW_PADDING, 282, `${antallBoker} bøker i produksjon`, {
        fontFamily: FONTS.body,
        fontSize: fontStr(18),
        color: '#3a2e22',
      })
      .setOrigin(0, 0);
    container.add(bokerTxt);

    const karriereKnapp = this.byggKarriereKnapp(
      ROW_WIDTH - ROW_PADDING,
      278,
      forfatter,
    );
    container.add(karriereKnapp);

    const lojRad = this.byggLojalitetRad(
      ROW_PADDING,
      318,
      ROW_WIDTH - ROW_PADDING * 2,
      forfatter.lojalitet,
    );
    container.add(lojRad);

    const action = this.byggKursAction(ROW_PADDING, 372, ROW_WIDTH - ROW_PADDING * 2, forfatter);
    container.add(action);

    return { container, height: høyde };
  }

  private byggKarriereKnapp(
    rightX: number,
    y: number,
    forfatter: Author,
  ): Phaser.GameObjects.Container {
    const c = this.scene.add.container(0, 0);
    const padX = 14;
    const padY = 8;
    const txt = this.scene.add
      .text(0, padY, 'Karriere →', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(16),
        color: '#862736',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    const w = txt.width + padX * 2;
    const h = txt.height + padY * 2;
    const bg = this.scene.add
      .rectangle(0, 0, w, h, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.wine, 0.8);
    txt.setPosition(padX, padY);
    c.add([bg, txt]);
    c.setPosition(rightX - w, y);
    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setAlpha(0.85));
    bg.on('pointerout', () => bg.setAlpha(1));
    bg.on('pointerup', () => this.openKarriereModal(forfatter.id));
    return c;
  }

  private openKarriereModal(authorId: string): void {
    if (!this.karriereModal) {
      this.karriereModal = new ForfatterKarriereModal(this.scene);
    }
    this.karriereModal.open(authorId);
  }

  private closeKarriereModal(): void {
    this.karriereModal?.close();
  }

  private byggLojalitetRad(
    x: number,
    y: number,
    w: number,
    lojalitet: number,
  ): Phaser.GameObjects.Container {
    const c = this.scene.add.container(x, y);
    const visning = lojalitetVisning(lojalitet);
    const fargeHex = `#${visning.farge.toString(16).padStart(6, '0')}`;

    const label = this.scene.add
      .text(0, 0, 'Lojalitet', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(14),
        color: '#3a2e22',
        fontStyle: '600',
      })
      .setOrigin(0, 0);
    c.add(label);

    const status = this.scene.add
      .text(w, 0, visning.label, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(14),
        color: fargeHex,
        fontStyle: '700',
      })
      .setOrigin(1, 0);
    c.add(status);

    const verdi = this.scene.add
      .text(w - status.width - 10, 0, `${Math.round(lojalitet)}`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(14),
        color: '#1a1a1a',
        fontStyle: '700',
      })
      .setOrigin(1, 0);
    c.add(verdi);

    const barY = 36;
    const barBg = this.scene.add
      .rectangle(0, barY, w, 8, COLORS.ochre, 0.3)
      .setOrigin(0, 0);
    const fyll = Math.max(2, Math.round(w * (lojalitet / 100)));
    const barFyll = this.scene.add
      .rectangle(0, barY, fyll, 8, visning.farge, 1)
      .setOrigin(0, 0);
    c.add([barBg, barFyll]);

    return c;
  }

  private byggPortrett(
    container: Phaser.GameObjects.Container,
    cx: number,
    cy: number,
    radius: number,
    forfatter: Author,
    tier: Tier,
  ): void {
    const key = portrettFor(forfatter.primaerSjanger, tier);
    const harBilde = this.scene.textures.exists(key);

    const ramme = this.scene.add.circle(cx, cy, radius + 2, COLORS.oakDark, 1);
    container.add(ramme);

    if (harBilde) {
      const bilde = this.scene.add.image(cx, cy, key);
      const src = this.scene.textures.get(key).getSourceImage() as HTMLImageElement;
      const srcW = src.width || radius * 2;
      const srcH = src.height || radius * 2;
      const skala = (radius * 2) / Math.min(srcW, srcH);
      bilde.setScale(skala);
      const maske = this.scene.make.graphics({ x: 0, y: 0 }, false);
      maske.fillStyle(0xffffff);
      maske.fillCircle(cx, cy, radius);
      bilde.setMask(maske.createGeometryMask());
      container.add(bilde);
    } else {
      const fyll = this.scene.add.circle(cx, cy, radius, COLORS.oakLight, 1);
      container.add(fyll);
      const initialer = this.scene.add
        .text(cx, cy, initialerAv(forfatter.navn), {
          fontFamily: FONTS.display,
          fontSize: fontStr(36),
          color: '#3a2410',
          fontStyle: '700',
        })
        .setOrigin(0.5);
      container.add(initialer);
    }
  }

  private byggStatusPille(
    rightX: number,
    y: number,
    forfatter: Author,
    dag: number,
  ): Phaser.GameObjects.Container {
    const c = this.scene.add.container(0, 0);
    const erPaaKurs = !!forfatter.paaKurs;
    const erSkriver = !erPaaKurs && !!forfatter.arbeiderPaa;
    let farge: number = COLORS.forest;
    let tekst = 'Aktiv';
    let progress = 0;
    if (erPaaKurs && forfatter.paaKurs) {
      farge = COLORS.ochre;
      const igjen = Math.max(0, forfatter.paaKurs.ferdigDag - dag);
      tekst = `På kurs (${igjen} dager igjen)`;
    } else if (erSkriver && forfatter.arbeiderPaa) {
      farge = COLORS.ochre;
      const igjen = Math.max(0, forfatter.arbeiderPaa.ferdigDag - dag);
      tekst = `Skriver bok (${igjen} dager igjen)`;
      const total = Math.max(
        1,
        forfatter.arbeiderPaa.ferdigDag - forfatter.arbeiderPaa.startDag,
      );
      const fullført = total - igjen;
      progress = Math.max(0, Math.min(1, fullført / total));
    }
    const padX = 14;
    const padY = 6;
    const txt = this.scene.add
      .text(0, padY, tekst, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(16),
        color: '#ffffff',
        fontStyle: '600',
      })
      .setOrigin(0, 0);
    const w = txt.width + padX * 2;
    const h = txt.height + padY * 2;
    const bg = lagRundetRekt(this.scene, 0, 0, w, h, farge, 1, 6);
    txt.setPosition(padX, padY);
    c.add([bg, txt]);
    if (erSkriver) {
      const progBg = this.scene.add
        .rectangle(0, h, w, 4, COLORS.oakLight, 1)
        .setOrigin(0, 0);
      const progFyll = this.scene.add
        .rectangle(0, h, Math.max(2, Math.round(w * progress)), 4, COLORS.forest, 1)
        .setOrigin(0, 0);
      c.add([progBg, progFyll]);
    }
    c.setPosition(rightX - w, y);
    return c;
  }

  private byggStatRad(
    x: number,
    y: number,
    statKeys: ReadonlyArray<keyof AuthorStats>,
    stats: AuthorStats,
  ): Phaser.GameObjects.Container {
    const c = this.scene.add.container(x, y);
    const totalW = ROW_WIDTH - ROW_PADDING * 2;
    const cellW = totalW / statKeys.length;
    const barBredde = cellW - 30;

    for (let i = 0; i < statKeys.length; i += 1) {
      const k = statKeys[i];
      const verdi = stats[k];
      const cellX = i * cellW;

      const label = this.scene.add
        .text(cellX, 0, STAT_LABEL[k], {
          fontFamily: FONTS.hud,
          fontSize: fontStr(12),
          color: '#3a2e22',
          fontStyle: '600',
        })
        .setOrigin(0, 0);
      const verdiTxt = this.scene.add
        .text(cellX + label.width + 6, 0, String(verdi), {
          fontFamily: FONTS.hud,
          fontSize: fontStr(12),
          color: '#1a1a1a',
          fontStyle: '700',
        })
        .setOrigin(0, 0);

      const barBg = this.scene.add
        .rectangle(cellX, 34, barBredde, 8, COLORS.ochre, 0.3)
        .setOrigin(0, 0);
      const fyll = Math.max(2, Math.round(barBredde * (verdi / 100)));
      const barFyll = this.scene.add
        .rectangle(cellX, 34, fyll, 8, fargeForVerdi(k, verdi), 1)
        .setOrigin(0, 0);
      c.add([label, verdiTxt, barBg, barFyll]);
    }

    return c;
  }

  private byggKursAction(
    x: number,
    y: number,
    w: number,
    forfatter: Author,
  ): Phaser.GameObjects.Container {
    const c = this.scene.add.container(x, y);
    const h = 60;

    const erPaaKurs = !!forfatter.paaKurs;
    const fill = erPaaKurs ? 0xcfc6b7 : COLORS.wine;
    const farge = erPaaKurs ? '#7a7060' : '#ffffff';
    const tekst = erPaaKurs ? 'Allerede på kurs' : 'Send på kurs →';

    const bg = lagRundetRekt(this.scene, 0, 0, w, h, fill, 1, 8);
    const txt = this.scene.add
      .text(w / 2, h / 2, tekst, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: farge,
        fontStyle: '700',
      })
      .setOrigin(0.5);
    c.add([bg, txt]);

    if (!erPaaKurs) {
      bg.setInteractive({ useHandCursor: true });
      bg.on('pointerover', () => bg.setAlpha(0.85));
      bg.on('pointerout', () => bg.setAlpha(1));
      bg.on('pointerup', () => this.openKursModal(forfatter));
    }
    return c;
  }

  private tellBoker(boker: { authorId: string }[], authorId: string): number {
    let n = 0;
    for (const b of boker) if (b.authorId === authorId) n += 1;
    return n;
  }

  private finnHoyesteTier(
    iProd: { authorId: string; tier: Tier }[],
    utgitt: { authorId: string; tier: Tier }[],
    authorId: string,
  ): Tier {
    const rangering: Tier[] = ['D', 'C', 'B', 'A', 'S'];
    let beste: Tier = 'D';
    const sjekk = (t: Tier): void => {
      if (rangering.indexOf(t) > rangering.indexOf(beste)) beste = t;
    };
    for (const b of iProd) if (b.authorId === authorId) sjekk(b.tier);
    for (const b of utgitt) if (b.authorId === authorId) sjekk(b.tier);
    return beste;
  }

  private openKursModal(forfatter: Author): void {
    this.closeKursModal();
    this.kursLayer = this.scene.add.container(0, 0).setDepth(140);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.65)
      .setOrigin(0, 0)
      .setInteractive();
    this.kursLayer.add(backdrop);

    const sheetX = (GAME_WIDTH - SHEET_WIDTH) / 2;
    const sheetY = 140;
    const sheetH = GAME_HEIGHT - 280;

    const sheet = this.scene.add
      .rectangle(sheetX, sheetY, SHEET_WIDTH, sheetH, COLORS.beige, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(3, COLORS.wine, 0.8);
    this.kursLayer.add(sheet);

    const header = this.scene.add
      .text(sheetX + ROW_PADDING, sheetY + 28, `Kurs for ${forfatter.navn}`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(36),
        color: '#862736',
      })
      .setOrigin(0, 0);
    this.kursLayer.add(header);

    const undertekst = this.scene.add
      .text(sheetX + ROW_PADDING, sheetY + 78, 'Forfatterskolen.no — anbefalt for forfatterens svakheter', {
        fontFamily: FONTS.body,
        fontSize: fontStr(18),
        color: '#3a2e22',
        fontStyle: 'italic',
      })
      .setOrigin(0, 0);
    this.kursLayer.add(undertekst);

    const closeBtn = this.scene.add
      .text(sheetX + SHEET_WIDTH - ROW_PADDING, sheetY + 16, '×', {
        fontFamily: FONTS.display,
        fontSize: fontStr(56),
        color: '#1a1a1a',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    closeBtn.on('pointerup', () => this.closeKursModal());
    this.kursLayer.add(closeBtn);

    const alleKurs = courseCatalog.courses;
    const topp2 = courseCatalog.forSlott(forfatter.stats, 2);
    const anbefalte = new Set(
      topp2
        .filter((c) => harSignifikantSvakhet(forfatter.stats, c.statBoosts))
        .map((c) => c.id),
    );

    const listX = sheetX + ROW_PADDING;
    const listW = SHEET_WIDTH - ROW_PADDING * 2;
    let y = sheetY + 130;

    if (alleKurs.length === 0) {
      const tomTxt = this.scene.add
        .text(sheetX + SHEET_WIDTH / 2, sheetY + sheetH / 2, 'Ingen kurs tilgjengelig.', {
          fontFamily: FONTS.body,
          fontSize: fontStr(24),
          color: '#3a2e22',
        })
        .setOrigin(0.5);
      this.kursLayer.add(tomTxt);
      return;
    }

    for (const kurs of alleKurs) {
      const anbefalt = anbefalte.has(kurs.id);
      const kort = this.byggKursKort(listX, y, listW, kurs, forfatter, anbefalt);
      this.kursLayer.add(kort.container);
      y += kort.height + 16;
    }
  }

  private byggKursKort(
    x: number,
    y: number,
    w: number,
    kurs: Course,
    forfatter: Author,
    anbefalt: boolean,
  ): { container: Phaser.GameObjects.Container; height: number } {
    const c = this.scene.add.container(x, y);
    const h = 220;

    const bg = this.scene.add
      .rectangle(0, 0, w, h, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, anbefalt ? 0xb8860b : COLORS.oakDark, anbefalt ? 0.8 : 0.4);
    c.add(bg);

    const tittel = this.scene.add
      .text(20, 16, kurs.title, {
        fontFamily: FONTS.display,
        fontSize: fontStr(24),
        color: '#1a1a1a',
        wordWrap: { width: w - 220 },
      })
      .setOrigin(0, 0);
    c.add(tittel);

    const meta = this.scene.add
      .text(20, 64, `${kurs.varighetDager} dager`, {
        fontFamily: FONTS.body,
        fontSize: fontStr(16),
        color: '#3a2e22',
      })
      .setOrigin(0, 0);
    c.add(meta);

    const pris = this.scene.add
      .text(w - 20, 16, `${kurs.pris.toLocaleString('nb-NO')} kr`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(24),
        color: '#862736',
      })
      .setOrigin(1, 0);
    c.add(pris);

    if (anbefalt) {
      const tagBg = this.scene.add
        .rectangle(w - 20, 64, 0, 0, 0xb8860b, 1)
        .setOrigin(1, 0);
      const tagTxt = this.scene.add
        .text(w - 28, 70, 'Anbefalt', {
          fontFamily: FONTS.hud,
          fontSize: fontStr(13),
          color: '#ffffff',
          fontStyle: '700',
        })
        .setOrigin(1, 0);
      tagBg.setSize(tagTxt.width + 18, tagTxt.height + 10);
      c.add([tagBg, tagTxt]);
    }

    let boostX = 20;
    const boostY = 110;
    for (const [k, v] of Object.entries(kurs.statBoosts)) {
      if (!v) continue;
      const key = k as keyof AuthorStats;
      const plus = this.scene.add
        .text(boostX, boostY, `+${v}`, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(18),
          color: '#862736',
          fontStyle: '700',
        })
        .setOrigin(0, 0);
      const navn = this.scene.add
        .text(boostX + plus.width + 4, boostY + 2, ` ${STAT_LABEL[key]}`, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(16),
          color: '#3a2e22',
        })
        .setOrigin(0, 0);
      c.add([plus, navn]);
      boostX += plus.width + navn.width + 18;
    }

    const snap = gameState.snapshot;
    const harRaad = snap.kroner >= kurs.pris;
    const knappW = w - 40;
    const knappX = 20;
    const knappY = h - 68;
    const knappH = 56;

    const knapp = this.scene.add.container(knappX, knappY);
    const fill = harRaad ? COLORS.wine : 0xcfc6b7;
    const farge = harRaad ? '#ffffff' : '#7a7060';
    const tekst = harRaad
      ? `Send ${forfatter.navn} på kurs`
      : 'For lite kapital';

    const kBg = lagRundetRekt(this.scene, 0, 0, knappW, knappH, fill, 1, 8);
    const kTxt = this.scene.add
      .text(knappW / 2, knappH / 2, tekst, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(18),
        color: farge,
        fontStyle: '700',
      })
      .setOrigin(0.5);
    knapp.add([kBg, kTxt]);

    if (harRaad) {
      kBg.setInteractive({ useHandCursor: true });
      kBg.on('pointerover', () => kBg.setAlpha(0.85));
      kBg.on('pointerout', () => kBg.setAlpha(1));
      kBg.on('pointerup', () => this.openConfirm(forfatter, kurs));
    }
    c.add(knapp);

    return { container: c, height: h };
  }

  private openConfirm(forfatter: Author, kurs: Course): void {
    this.closeConfirm();
    this.confirmLayer = this.scene.add.container(0, 0).setDepth(160);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6)
      .setOrigin(0, 0)
      .setInteractive();
    this.confirmLayer.add(backdrop);

    const dW = 880;
    const dH = 520;
    const dX = (GAME_WIDTH - dW) / 2;
    const dY = (GAME_HEIGHT - dH) / 2;

    const sheet = lagRundetRekt(
      this.scene,
      dX,
      dY,
      dW,
      dH,
      COLORS.white,
      1,
      14,
    ).setStrokeStyle(3, COLORS.wine, 0.8);
    this.confirmLayer.add(sheet);

    const title = this.scene.add
      .text(dX + dW / 2, dY + 48, 'Send på kurs?', {
        fontFamily: FONTS.display,
        fontSize: fontStr(40),
        color: '#862736',
      })
      .setOrigin(0.5, 0);
    this.confirmLayer.add(title);

    const body = this.scene.add
      .text(
        dX + dW / 2,
        dY + 156,
        `Send ${forfatter.navn} på «${kurs.title}»\nfor ${kurs.pris.toLocaleString('nb-NO')} kr?\n\nHen kommer tilbake om ${kurs.varighetDager} dager.`,
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(22),
          color: '#1a1a1a',
          align: 'center',
          wordWrap: { width: dW - 64 },
        },
      )
      .setOrigin(0.5, 0);
    this.confirmLayer.add(body);

    const btnY = dY + dH - 120;
    const btnW = 320;
    const gap = 32;
    const totalBtnW = btnW * 2 + gap;
    const startX = dX + (dW - totalBtnW) / 2;

    const avbryt = this.lagKnapp(startX, btnY, btnW, 'Avbryt', false, () => this.closeConfirm());
    this.confirmLayer.add(avbryt);

    const ja = this.lagKnapp(startX + btnW + gap, btnY, btnW, 'Ja, send på kurs', true, () => {
      const ok = gameState.sendPaaKurs(forfatter.id, kurs.id);
      this.closeConfirm();
      if (ok) this.closeKursModal();
    });
    this.confirmLayer.add(ja);
  }

  private lagKnapp(
    x: number,
    y: number,
    w: number,
    label: string,
    primary: boolean,
    onClick: () => void,
  ): Phaser.GameObjects.Container {
    const c = this.scene.add.container(x, y);
    const h = 64;
    const fill = primary ? COLORS.wine : 0xffffff;
    const farge = primary ? '#ffffff' : '#862736';
    const bg = lagRundetRekt(this.scene, 0, 0, w, h, fill, 1, 8);
    if (!primary) bg.setStrokeStyle(3, COLORS.wine, 1);
    const txt = this.scene.add
      .text(w / 2, h / 2, label, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: farge,
        fontStyle: '700',
      })
      .setOrigin(0.5);
    c.add([bg, txt]);
    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setAlpha(0.85));
    bg.on('pointerout', () => bg.setAlpha(1));
    bg.on('pointerup', onClick);
    return c;
  }

  private closeKursModal(): void {
    this.kursLayer?.destroy(true);
    this.kursLayer = undefined;
  }

  private closeConfirm(): void {
    this.confirmLayer?.destroy(true);
    this.confirmLayer = undefined;
  }
}

function harSignifikantSvakhet(
  stats: AuthorStats,
  boosts: Partial<AuthorStats>,
): boolean {
  for (const k of Object.keys(boosts) as (keyof AuthorStats)[]) {
    const v = boosts[k];
    if (typeof v !== 'number' || v <= 0) continue;
    if (stats[k] < 50) return true;
  }
  return false;
}
